// First-touch signup attribution.
//
// A client component (AttributionCapture) writes a single first-touch `pr_attr`
// cookie on the visitor's first external/UTM-tagged landing. At signup time the
// server reads that cookie and persists it onto the user row (signupSource +
// signupAttribution). Everything here is best-effort: attribution must never
// break signup, so callers should treat parse failures as "no attribution".

export const ATTRIBUTION_COOKIE = 'pr_attr';

export interface AttributionData {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  referrer?: string;
  landing_page?: string;
  captured_at?: string;
}

/**
 * Derive the short `signup_source` label from attribution data.
 * Prefers utm_source; falls back to the referrer hostname (www. stripped).
 * Truncated to 100 chars. Returns null when nothing usable is present.
 */
export function deriveSignupSource(attr: AttributionData | null): string | null {
  if (!attr) return null;

  let source = attr.utm_source?.trim() || '';

  if (!source && attr.referrer) {
    try {
      const host = new URL(attr.referrer).hostname.replace(/^www\./, '');
      source = host;
    } catch {
      // referrer wasn't a parseable URL — ignore
    }
  }

  source = source.trim();
  if (!source) return null;
  return source.slice(0, 100);
}

/**
 * Parse the raw `pr_attr` cookie value (URI-encoded JSON) into AttributionData.
 * Returns null on any failure.
 */
export function parseAttributionCookieValue(raw: string | undefined | null): AttributionData | null {
  if (!raw) return null;
  try {
    const decoded = decodeURIComponent(raw);
    const parsed = JSON.parse(decoded);
    if (parsed && typeof parsed === 'object') {
      return parsed as AttributionData;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Parse the `pr_attr` cookie out of a raw Cookie header string.
 * Used by route handlers that have a NextRequest.
 */
export function parseAttributionFromCookieHeader(cookieHeader: string | null | undefined): AttributionData | null {
  if (!cookieHeader) return null;
  try {
    const match = cookieHeader
      .split(';')
      .map((c) => c.trim())
      .find((c) => c.startsWith(`${ATTRIBUTION_COOKIE}=`));
    if (!match) return null;
    const value = match.slice(ATTRIBUTION_COOKIE.length + 1);
    return parseAttributionCookieValue(value);
  } catch {
    return null;
  }
}

/**
 * Resolve the two persisted columns from attribution data.
 * Returns nulls when there's nothing to store.
 */
export function resolveSignupAttribution(attr: AttributionData | null): {
  signupSource: string | null;
  signupAttribution: string | null;
} {
  if (!attr) return { signupSource: null, signupAttribution: null };
  const signupSource = deriveSignupSource(attr);
  let signupAttribution: string | null = null;
  try {
    signupAttribution = JSON.stringify(attr);
  } catch {
    signupAttribution = null;
  }
  if (!signupSource && !signupAttribution) {
    return { signupSource: null, signupAttribution: null };
  }
  return { signupSource, signupAttribution };
}
